import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown } from 'lucide-react';

const PROJECT_TYPES = [
  'AI product build',
  'Agents and automation',
  'Mobile app',
  'Web app',
  'Advisory',
  'A full time role',
  'Something else',
];

const BUDGETS = [
  'Under $5k',
  '$5k to $15k',
  '$15k to $50k',
  '$50k+',
  'Not sure yet',
];

const CustomSelect = ({ label, name, value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-[#051A24] mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full px-4 py-3 rounded-xl border text-left text-sm flex items-center justify-between transition-colors bg-white ${
          open ? 'border-[#051A24] ring-1 ring-[#051A24]' : 'border-[#E0EBF0]'
        } ${value ? 'text-[#051A24]' : 'text-[#273C46]/50'}`}
        data-testid={testId}
      >
        <span className="truncate">{value || 'Select...'}</span>
        <ChevronDown className={`w-4 h-4 text-[#273C46] flex-shrink-0 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-[#E0EBF0] rounded-xl shadow-lg overflow-hidden py-1 max-h-48 overflow-y-auto">
          {options.map((opt) => (
            <li key={opt}>
              <button
                type="button"
                onClick={() => { onChange({ target: { name, value: opt } }); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-[#E0EBF0]/50 ${
                  value === opt ? 'text-[#051A24] font-medium bg-[#E0EBF0]/30' : 'text-[#273C46]'
                }`}
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to send message');
      }

      setIsSubmitted(true);

      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', company: '', projectType: '', budget: '', message: '' });
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="contact-modal-overlay"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#051A24]/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="contact-modal-backdrop"
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 transform transition-all max-h-[90vh] overflow-y-auto"
        data-testid="contact-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#E0EBF0] transition-colors"
          data-testid="contact-modal-close"
        >
          <X className="w-5 h-5 text-[#273C46]" />
        </button>

        {isSubmitted ? (
          <div className="text-center py-8" data-testid="contact-success">
            <div className="w-16 h-16 bg-[#051A24] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-medium text-[#051A24] mb-2">Message sent</h3>
            <p className="text-[#273C46]">I'll get back to you within 24 hours.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-medium text-[#051A24] mb-2">
                Let's <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">chat</span>
              </h2>
              <p className="text-[#273C46] text-sm">
                Tell me what you're building. I'll reply within 24 hours.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              {/* Honeypot. Hidden from people and screen readers; bots fill it in. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website || ''}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#051A24] mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-[#E0EBF0] focus:border-[#051A24] focus:ring-1 focus:ring-[#051A24] outline-none transition-colors text-[#051A24]"
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#051A24] mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-[#E0EBF0] focus:border-[#051A24] focus:ring-1 focus:ring-[#051A24] outline-none transition-colors text-[#051A24]"
                  placeholder="you@company.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-[#051A24] mb-1">
                  Company <span className="text-[#273C46]/50 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#E0EBF0] focus:border-[#051A24] focus:ring-1 focus:ring-[#051A24] outline-none transition-colors text-[#051A24]"
                  placeholder="Your company"
                  data-testid="contact-company-input"
                />
              </div>

              {/* Project Type & Budget side by side */}
              <div className="grid grid-cols-2 gap-3">
                <CustomSelect
                  label="Project Type"
                  name="projectType"
                  value={formData.projectType}
                  options={PROJECT_TYPES}
                  onChange={handleChange}
                  testId="contact-project-type-select"
                />
                <CustomSelect
                  label="Budget"
                  name="budget"
                  value={formData.budget}
                  options={BUDGETS}
                  onChange={handleChange}
                  testId="contact-budget-select"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#051A24] mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-[#E0EBF0] focus:border-[#051A24] focus:ring-1 focus:ring-[#051A24] outline-none transition-colors resize-none text-[#051A24]"
                  placeholder="What are you building, and where are you stuck?"
                  data-testid="contact-message-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center" data-testid="contact-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-7 py-3 rounded-full font-medium text-sm bg-[#051A24] text-white hover:bg-[#0D212C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
